import { uniqBy } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Font } from "src/types";
import type * as WebFontType from "webfontloader";

import endpoints from "@/utils/endpoints";

type GoogleFontLoader = {
  type: "Google";
  text?: string;
  families: string[];
};

type CustomFontLoader = {
  type: "Custom";
  families: string[];
  pageId: number;
};

type FontLoader = GoogleFontLoader | CustomFontLoader;

const fontWeightsVariation = ":700,600,500,400,300";

const get = endpoints();

export default function useFontLoader(
  opts: FontLoader,
  loadOnInitial = true,
  onActive?: () => void
): () => void {
  const WebFont = useMemo(
    () => require("webfontloader"),
    []
  ) as typeof WebFontType;

  const {
    query: { slug },
  } = useRouter();

  const hasInitialLoadRef = useRef(false);

  const onActiveRef = useRef(onActive);
  onActiveRef.current = onActive;

  // Store local copy of "opts" since "opts" may not be stable (ie: not wrapped using useMemo)
  const [currentOpts, setCurrentOpts] = useState(opts);
  const config = useMemo(() => {
    const opts = currentOpts;

    return {
      // WebFont has issue with active call back in case of parallel loading
      // we currently use active callback only for fonts previews
      // so it shouldn't be a problem
      // https://github.com/typekit/webfontloader/issues/345
      // possible fix:
      // https://github.com/typekit/webfontloader/issues/309
      ...(onActiveRef.current && { active: onActiveRef.current }),
      ...(opts.type === "Google" && {
        google: {
          families: opts.families,
          ...(opts.text && { text: opts.text }),
        },
      }),
      ...(opts.type === "Custom" && {
        custom: {
          families: opts.families,
          urls: [
            get("CUSTOM_FONTS_CSS")(slug as string, opts.pageId.toString()),
          ],
        },
      }),
    };
  }, [currentOpts, slug]);

  // Sync client opts with currentOpts state
  useEffect(() => {
    if (opts.families.join(",") === currentOpts.families.join(",")) {
      return;
    }

    setCurrentOpts(opts);
  }, [opts, currentOpts, setCurrentOpts]);

  const load = useCallback(() => {
    hasInitialLoadRef.current = true;
    WebFont.load(config);
  }, [WebFont, config]);

  useEffect(() => {
    // Initial load not done
    if (!hasInitialLoadRef.current) {
      // Automatically call load() if loadOnInitial is true
      if (loadOnInitial !== false) {
        load();
      }

      return;
    }

    load();
  }, [loadOnInitial, load]);

  const loadRef = useRef(load);
  loadRef.current = load;

  // Expose a stable callback
  // Since we have an useEffect above that fires load() when the callback has changed
  // If we were to expose load() to the client, and if the client uses load() as a dep,
  // This will result in essentially the same as the hook above, therefore
  // resulting in duplicate calls
  const stableInitialLoadCallback = useCallback(() => {
    loadRef.current();
  }, []);

  return stableInitialLoadCallback;
}

export function getFontsByProvider(fonts: Font[], provider: string) {
  return uniqBy(fonts, (font) => font.value)
    .filter((font) => font.provider === provider)
    .map((font) => font.value + fontWeightsVariation);
}
